import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import "./planProductTools.css";
import { apiUrl, API_BASE } from "./apiBase";

const DATASETS = [
  { key: "sku_master", label: "SKU Master", required: true },
  { key: "site_master", label: "Site Master", required: true },
  { key: "sales", label: "Sales by Site", required: true },
  { key: "forecast", label: "Forecast Accuracy", required: false },
  { key: "supply", label: "Supply File", required: true },
  { key: "inventory", label: "Inventory File", required: true },
  { key: "reliability", label: "Reliability (Adherence)", required: false },
];

const StepShell = ({ step, title, subtitle, enabled, children }) => (
  <section
    className={
      "pp-glass pp-card pp-elevate pp-animate-in p-6 transition " +
      (enabled ? "opacity-100" : "opacity-45 pointer-events-none")
    }
  >
    <div className="flex items-start gap-3 mb-5">
      <div className="w-10 h-10 rounded-2xl bg-slate-950/50 border border-slate-700/70 text-slate-100 font-bold grid place-items-center shadow-lg shadow-black/40">
        {step}
      </div>
      <div>
        <h2 className="text-2xl sm:text-3xl font-bold">{title}</h2>
        {subtitle ? <p className="text-slate-400">{subtitle}</p> : null}
      </div>
    </div>
    {children}
  </section>
);

const UploadIcon = ({ className = "w-4 h-4" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
  >
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <path d="M7 10l5-5 5 5" />
    <path d="M12 5v11" />
  </svg>
);

const UploadTile = ({ label, required, status, onUpload }) => {
  const inputRef = useRef(null);
  const busy = status === "uploading";
  const done = status === "uploaded";
  const openFilePicker = () => {
    if (busy) return;
    inputRef.current?.click();
  };
  return (
    <div className="pp-glass pp-elevate pp-upload-tile rounded-2xl px-4 py-4 flex flex-col gap-3 shadow-lg shadow-black/30">
      <div className="flex items-center gap-2 text-slate-100 font-semibold text-lg">
        <span className="inline-flex items-center justify-center w-8 h-8 rounded-xl bg-slate-950/40 border border-slate-700/60 text-slate-200">
          <UploadIcon className="w-4 h-4" />
        </span>
        <span className="truncate">{label}</span>{" "}
        {required ? (
          <span className="text-slate-400 text-sm">(required)</span>
        ) : (
          <span className="text-slate-500 text-sm">(optional)</span>
        )}
        {done ? <span className="ml-2 text-emerald-400 text-sm">Uploaded</span> : null}
      </div>
      <div className="relative">
        <input
          ref={inputRef}
          type="file"
          className="hidden"
          accept=".csv,.xlsx,.xls,.txt,.parquet"
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = "";
            if (file) onUpload(file);
          }}
        />
        <div
          role="button"
          tabIndex={busy ? -1 : 0}
          aria-disabled={busy}
          onClick={openFilePicker}
          onKeyDown={(e) => {
            if (busy) return;
            if (e.key === "Enter" || e.key === " ") openFilePicker();
          }}
          className={`pp-upload-drop ${busy ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
        >
          <div className="flex items-center gap-3 min-w-0">
            {/* <div
              className={`w-9 h-9 rounded-xl grid place-items-center border ${
                done
                  ? "bg-emerald-500/15 text-emerald-200 border-emerald-400/25"
                  : "bg-slate-950/35 text-slate-200 border-slate-700/60"
              }`}
            >
              {done ? "✓" : <UploadIcon className="w-2 h-8" />}
            </div> */}
            <div className="min-w-0">
              <div className="text-slate-200 font-semibold text-sm">
                {done ? "Ready to replace" : "Upload file"}
              </div>
              <div className="text-xs text-slate-500 truncate">
                {done ? "Click to choose a new file" : "CSV, XLSX, XLS, TXT, Parquet"}
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              openFilePicker();
            }}
            disabled={busy}
            className="pp-upload-cta"
          >
            <UploadIcon className="w-4 h-4" />
            {done ? "Replace file" : "Select file"}
          </button>
        </div>

        {busy ? (
          <div className="absolute inset-0 bg-black/35 rounded-2xl backdrop-blur-sm flex items-center justify-center text-slate-200 text-sm">
            Uploading…
          </div>
        ) : null}
      </div>
    </div>
  );
};

const Toast = ({ msg, err }) => (
  <div
    className={`fixed bottom-6 left-1/2 -translate-x-1/2 px-4 py-2 rounded-2xl text-sm font-semibold shadow-lg pp-animate-in ${
      err
        ? "bg-red-500/90 border border-red-300/20"
        : "pp-glass border border-slate-800/70"
    }`}
  >
    {msg}
  </div>
);

const PipelineCard = ({ title, subtitle, status, onRun, runLabel, disabled }) => {
  const done = status === "done";
  const current = status === "running";
  return (
    <div className="pp-glass pp-elevate rounded-2xl border border-slate-800/70 px-4 py-3 flex items-center gap-3 justify-between">
      <div className="flex items-center gap-3">
        <div
          className={`w-10 h-10 rounded-2xl grid place-items-center text-lg border ${
            done
              ? "bg-emerald-500/15 text-emerald-300 border-emerald-400/25"
              : current
              ? "bg-sky-500/15 text-sky-300 border-sky-400/25"
              : "bg-slate-950/35 text-slate-500 border-slate-700/50"
          } ${current ? "animate-pulse" : ""}`}
        >
          {done ? "✓" : current ? "⏺" : "•"}
        </div>
        <div>
          <div className="text-slate-100 font-semibold">{title}</div>
          <div className="text-slate-400 text-sm">{subtitle}</div>
        </div>
      </div>
      {onRun ? (
        <button
          type="button"
          onClick={onRun}
          disabled={disabled}
          className="pp-chip text-sm font-semibold disabled:opacity-50"
        >
          {runLabel}
        </button>
      ) : null}
    </div>
  );
};

async function postJson(url, payload) {
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`${res.status} ${res.statusText}: ${text}`);
  }
  return res.json();
}

function sleep(ms) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function prettifyStage(stage) {
  const s = String(stage || "").trim();
  if (!s) return "";
  const map = {
    "baseline.loading": "Loading inputs",
    "baseline.compute": "Computing baseline",
    "baseline.export": "Exporting workbook",
    "analysis.loading": "Loading inputs",
    "analysis.compute": "Computing analysis",
    "analysis.export": "Exporting workbook",
  };
  return map[s] || s.replace(/\./g, " · ");
}

async function waitForJob({
  jobId,
  statusUrl,
  timeoutMs = 45 * 60 * 1000,
  onUpdate,
}) {
  const startedAt = Date.now();
  const url = statusUrl ? apiUrl(statusUrl) : apiUrl(`/api/jobs/${jobId}`);
  while (true) {
    const res = await fetch(url, { method: "GET" });
    if (!res.ok) {
      const txt = await res.text();
      throw new Error(`Job status failed (${res.status}): ${txt}`);
    }
    const data = await res.json();
    if (onUpdate) {
      try {
        onUpdate(data);
      } catch (_e) {
        // ignore
      }
    }
    const state = data?.state || data?.status;
    if (state === "succeeded") return data;
    if (state === "failed") {
      throw new Error(data?.error || "Job failed");
    }
    if (Date.now() - startedAt > timeoutMs) {
      throw new Error("Timed out waiting for job to finish");
    }
    await sleep(2000);
  }
}

export default function PlanProductConsultant() {
  const [uploads, setUploads] = useState(() =>
    DATASETS.reduce((acc, d) => ({ ...acc, [d.key]: "idle" }), {})
  );
  const [configYaml, setConfigYaml] = useState("");
  const [qcStatus, setQcStatus] = useState("idle");
  const [baselineStatus, setBaselineStatus] = useState("idle");
  const [analysisStatus, setAnalysisStatus] = useState("idle");
  const [baselineLink, setBaselineLink] = useState("");
  const [analysisLink, setAnalysisLink] = useState("");
  const [qcSummary, setQcSummary] = useState([]);
  const [serviceLevels, setServiceLevels] = useState([]);
  const [toast, setToast] = useState(null);
  const [configStatus, setConfigStatus] = useState("Not uploaded");
  const [params, setParams] = useState({
    months: 12,
    a: 0.8,
    b: 0.95,
    xyzLow: 33,
    xyzHigh: 67,
    minSSDays: "",
    maxSSDays: "",
  });
  const configFileInput = useRef(null);
  const [storageStatus, setStorageStatus] = useState(null);
  const [storageStatusErr, setStorageStatusErr] = useState("");
  const [storageStatusBusy, setStorageStatusBusy] = useState(false);
  const [qcSampleRows, setQcSampleRows] = useState("");
  const [pipelineHint, setPipelineHint] = useState("");
  const [pipelineKind, setPipelineKind] = useState("");

  const showToast = (msg, err = false) => {
    setToast({ msg, err });
    window.setTimeout(() => setToast(null), 2200);
  };

  const pipelineRunning = useMemo(
    () => qcStatus === "running" || baselineStatus === "running" || analysisStatus === "running",
    [qcStatus, baselineStatus, analysisStatus]
  );

  const refreshStorageStatus = useCallback(async () => {
    setStorageStatusBusy(true);
    setStorageStatusErr("");
    try {
      const res = await fetch(apiUrl("/api/storage/status"));
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`${res.status} ${res.statusText}: ${text}`);
      }
      const data = await res.json();
      setStorageStatus(data);
    } catch (err) {
      console.error(err);
      setStorageStatus(null);
      setStorageStatusErr(err?.message || String(err));
    } finally {
      setStorageStatusBusy(false);
    }
  }, []);

  const allRequiredUploaded = useMemo(
    () => DATASETS.filter((d) => d.required).every((d) => uploads[d.key] === "uploaded"),
    [uploads]
  );
  const step1Done = allRequiredUploaded;
  const step2Enabled = step1Done;
  const step3Enabled = step1Done && Boolean(configYaml);

  const handleUpload = async (key, file) => {
    setUploads((s) => ({ ...s, [key]: "uploading" }));
    const form = new FormData();
    form.append("dataset", key);
    form.append("file", file);
    try {
      const res = await fetch(apiUrl("/api/upload"), { method: "POST", body: form });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(`Upload failed (${res.status}): ${txt}`);
      }
      const payload = await res.json();
      setUploads((s) => ({ ...s, [key]: "uploaded" }));
      showToast(`${file.name} uploaded (${payload.storage || "?"})`);
      refreshStorageStatus();
    } catch (err) {
      console.error(err);
      setUploads((s) => ({ ...s, [key]: "idle" }));
      showToast("Upload failed", true);
    }
  };

  const generateConfig = async () => {
    if (!step1Done) return;
    try {
      const data = await postJson(apiUrl("/api/config/generate"), {});
      setConfigYaml(data.yaml || "");
      setConfigStatus("Generated config");
      setQcStatus("idle");
      setBaselineStatus("idle");
      setAnalysisStatus("idle");
      showToast("Config generated");
    } catch (err) {
      console.error(err);
      showToast("Config generation failed", true);
    }
  };

  const uploadConfig = async (file) => {
    try {
      const text = await file.text();
      setConfigYaml(text);
      setConfigStatus(`Uploaded: ${file.name}`);
      setQcStatus("idle");
      setBaselineStatus("idle");
      setAnalysisStatus("idle");
      showToast("Config uploaded");
    } catch (err) {
      console.error(err);
      showToast("Failed to read config", true);
    }
  };

  const runQCOnly = async () => {
    if (!step3Enabled) return;
    setQcStatus("running");
    try {
      const qcUrl = qcSampleRows
        ? `${apiUrl("/api/qc")}?sample_rows=${encodeURIComponent(qcSampleRows)}`
        : apiUrl("/api/qc");
      const qcData = await postJson(qcUrl, { config_yaml: configYaml });
      if (qcData.errors) {
        showToast("QC errors found", true);
        setQcSummary(qcData.errors.map((e, i) => ({ idx: i, error: e })));
        setQcStatus("idle");
        return;
      }
      setQcSummary(qcData.qc || []);
      setQcStatus("done");
      showToast("QC completed");
    } catch (err) {
      console.error(err);
      setQcStatus("idle");
      showToast("QC failed", true);
    }
  };

  const runBaselineOnly = async () => {
    if (!step3Enabled) return;
    setBaselineStatus("running");
    setBaselineLink("");
    try {
      const submit = await postJson(apiUrl("/api/baseline?async=1"), { config_yaml: configYaml });
      const jobId = submit.job_id;
      if (!jobId) throw new Error("Missing job_id from baseline response");
      showToast("Baseline started");
      const status = await waitForJob({ jobId, statusUrl: submit.status_url });
      setBaselineLink(status.download || submit.download || "");
      setBaselineStatus("done");
      showToast("Baseline completed");
    } catch (err) {
      console.error(err);
      setBaselineStatus("idle");
      showToast("Baseline failed", true);
    }
  };

  const runAnalysisOnly = async () => {
    if (!step3Enabled) return;
    setAnalysisStatus("running");
    setAnalysisLink("");
    try {
      const submit = await postJson(apiUrl("/api/analysis?async=1"), {
        config_yaml: configYaml,
        service_levels: serviceLevels,
        months_lookback: params.months,
        a_threshold: params.a,
        b_threshold: params.b,
        xyz_low: params.xyzLow,
        xyz_high: params.xyzHigh,
        min_ss_days: params.minSSDays === "" ? null : params.minSSDays,
        max_ss_days: params.maxSSDays === "" ? null : params.maxSSDays,
      });
      const jobId = submit.job_id;
      if (!jobId) throw new Error("Missing job_id from analysis response");
      showToast("Analysis started");
      const status = await waitForJob({ jobId, statusUrl: submit.status_url });
      setAnalysisLink(status.download || submit.download || "");
      setAnalysisStatus("done");
      showToast("Analysis completed");
    } catch (err) {
      console.error(err);
      setAnalysisStatus("idle");
      showToast("Analysis failed", true);
    }
  };

  const runPipeline = async () => {
    if (!step3Enabled) return;
    setQcStatus("running");
    setBaselineStatus("idle");
    setAnalysisStatus("idle");
    setBaselineLink("");
    setAnalysisLink("");
    setPipelineHint("Running quality check");
    setPipelineKind("qc");
    try {
      const qcUrl = qcSampleRows
        ? `${apiUrl("/api/qc")}?sample_rows=${encodeURIComponent(qcSampleRows)}`
        : apiUrl("/api/qc");
      const qcData = await postJson(qcUrl, { config_yaml: configYaml });
      if (qcData.errors) {
        showToast("QC errors found", true);
        setQcSummary(qcData.errors.map((e, i) => ({ idx: i, error: e })));
        setQcStatus("idle");
        return;
      }
      setQcSummary(qcData.qc || []);
      setQcStatus("done");

      setBaselineStatus("running");
      setPipelineKind("baseline");
      setPipelineHint("Baseline queued");
      const baseSubmit = await postJson(apiUrl("/api/baseline?async=1"), { config_yaml: configYaml });
      if (!baseSubmit.job_id) throw new Error("Missing job_id from baseline response");
      const baseStatus = await waitForJob({
        jobId: baseSubmit.job_id,
        statusUrl: baseSubmit.status_url,
        onUpdate: (d) => setPipelineHint(prettifyStage(d?.stage) || "Baseline running"),
      });
      setBaselineLink(baseStatus.download || baseSubmit.download || "");
      setBaselineStatus("done");

      setAnalysisStatus("running");
      setPipelineKind("analysis");
      setPipelineHint("Analysis queued");
      const anaSubmit = await postJson(apiUrl("/api/analysis?async=1"), {
        config_yaml: configYaml,
        service_levels: serviceLevels,
        months_lookback: params.months,
        a_threshold: params.a,
        b_threshold: params.b,
        xyz_low: params.xyzLow,
        xyz_high: params.xyzHigh,
        min_ss_days: params.minSSDays === "" ? null : params.minSSDays,
        max_ss_days: params.maxSSDays === "" ? null : params.maxSSDays,
      });
      if (!anaSubmit.job_id) throw new Error("Missing job_id from analysis response");
      const anaStatus = await waitForJob({
        jobId: anaSubmit.job_id,
        statusUrl: anaSubmit.status_url,
        onUpdate: (d) => setPipelineHint(prettifyStage(d?.stage) || "Analysis running"),
      });
      setAnalysisLink(anaStatus.download || anaSubmit.download || "");
      setAnalysisStatus("done");
      setPipelineKind("");
      setPipelineHint("");
      showToast("Pipeline completed");
    } catch (err) {
      console.error(err);
      setQcStatus("idle");
      setBaselineStatus("idle");
      setAnalysisStatus("idle");
      setPipelineKind("");
      setPipelineHint("");
      showToast("Pipeline failed", true);
    }
  };

  useEffect(() => {
    setStorageStatus(null);
    setStorageStatusErr("");

    refreshStorageStatus();

    (async () => {
      try {
        const res = await fetch(apiUrl("/api/service-levels"));
        const data = await res.json();
        setServiceLevels(data.service_levels || []);
      } catch (err) {
        console.error(err);
      }
    })();
  }, [refreshStorageStatus]);

  const downloadConfig = () => {
    if (!configYaml) return;
    const blob = new Blob([configYaml], { type: "text/yaml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "config.yaml";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="pp-page">
      <div className="pp-orb pp-orb--a" aria-hidden="true" />
      <div className="pp-orb pp-orb--b" aria-hidden="true" />
      <div className="pp-orb pp-orb--c" aria-hidden="true" />

      <header className="sticky top-0 z-30 h-16 border-b border-slate-800/70 bg-slate-950/35 backdrop-blur-xl flex items-center justify-between px-4 sm:px-6">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-tr from-emerald-400 to-teal-500 grid place-items-center text-white font-bold shadow-lg shadow-emerald-500/10">
            C
          </div>
          <div>
            <div className="font-bold text-lg leading-none">Consultant</div>
            <div className="text-xs text-slate-400">Plan Product • Workspace</div>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <Link to="/plan-product/parquet" className="pp-chip text-sm font-semibold">
            <span aria-hidden="true">🧩</span> Parquet
          </Link>
          <Link to="/plan-product-home" className="pp-chip text-sm font-semibold">
            <span aria-hidden="true">↩</span> Home
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8 space-y-10 relative">
        {/* <section className="pp-glass pp-card pp-animate-in p-5 border border-slate-800/70">
          <div className="flex flex-col md:flex-row md:items-end gap-4 justify-between">
            <div className="min-w-0">
              <div className="pp-gradient-text text-2xl sm:text-3xl font-bold leading-tight">
                Consultant workspace
              </div>
              <div className="text-slate-400 text-sm mt-1">
                Upload datasets, generate the config, then run QC, baseline, and analysis.
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <span className="pp-pill">Azure backend</span>
                <span className="pp-pill">Blob storage</span>
                <span className="pp-pill">{API_BASE.replace(/^https?:\/\//, "")}</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="pp-chip text-sm font-semibold disabled:opacity-50"
                disabled={storageStatusBusy}
                onClick={refreshStorageStatus}
              >
                {storageStatusBusy ? "Refreshing…" : "Refresh status"}
              </button>
            </div>
          </div>

          <div className="mt-4 flex flex-col md:flex-row gap-4 md:items-center justify-between">
            <div className="text-sm text-slate-300">
              <span className="text-slate-400">Connected:</span> {API_BASE}
              {storageStatus?.storage ? (
                <>
                  {"  "}
                  <span className="text-slate-400">Storage:</span> {storageStatus.storage}
                </>
              ) : null}
              {storageStatus?.storage === "blob" ? (
                <>
                  {"  "}
                  <span className="text-slate-400">Prefix:</span> {storageStatus.prefix}
                  {"  "}
                  <span className="text-slate-400">Manifest:</span>{" "}
                  {storageStatus.manifest_loaded_count || 0}
                </>
              ) : null}
            </div>
          </div>

          {storageStatusErr ? (
            <div className="mt-3 text-sm text-red-300">
              Status error: {storageStatusErr}
            </div>
          ) : null}

          {storageStatus ? (
            <pre className="mt-3 pp-glass border border-slate-800/70 rounded-2xl p-3 overflow-auto max-h-64 text-slate-200 text-xs">
              {JSON.stringify(storageStatus, null, 2)}
            </pre>
          ) : null}
        </section> */}

        <StepShell
          step="1"
          title="Data Ingestion"
          subtitle="Upload required datasets to initialize the model context. Supported: CSV, XLSX, XLS, TXT, Parquet."
          enabled
        >
          <div className="grid grid-cols-3 md:grid-cols-4 gap-4">
            {DATASETS.map((d) => (
              <UploadTile
                key={d.key}
                label={d.label}
                required={d.required}
                status={uploads[d.key]}
                onUpload={(f) => handleUpload(d.key, f)}
              />
            ))}
          </div>
        </StepShell>

        <StepShell
          step="2"
          title="Model Configuration"
          subtitle="Generate, audit, and refine the model parameters."
          enabled={step2Enabled}
        >
          <div className="pp-glass rounded-2xl border border-slate-800/70 p-4 flex flex-col gap-3">
            <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
              <div className="text-left">
                <div className="text-slate-100 font-semibold">Configuration File (config.yaml)</div>
                <div className="text-slate-500 text-sm">
                  Contains thresholds for segmentation, safety stock formulas, and risk parameters.
                </div>
                <div className="text-slate-300 text-sm mt-1">{configStatus}</div>
              </div>
              <div className="flex gap-3 flex-wrap">
                <button
                  type="button"
                  onClick={generateConfig}
                  disabled={!step2Enabled}
                  className="pp-chip disabled:opacity-50"
                >
                  Generate Config
                </button>
                <button
                  type="button"
                  onClick={() => configFileInput.current?.click()}
                  disabled={!step2Enabled}
                  className="pp-chip disabled:opacity-50"
                >
                  Upload Config
                </button>
                <button
                  type="button"
                  onClick={downloadConfig}
                  disabled={!configYaml}
                  className="pp-chip disabled:opacity-50"
                >
                  Download Config
                </button>
                <input
                  ref={configFileInput}
                  type="file"
                  accept=".yml,.yaml"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) uploadConfig(file);
                  }}
                />
              </div>
            </div>
          </div>
        </StepShell>

        <StepShell
          step="3"
          title="Optimization Pipeline"
          subtitle="Execute the core optimization logic."
          enabled={step3Enabled}
        >
          <div className="grid md:grid-cols-3 gap-4 mb-4">
            <PipelineCard
              title="Quality Check"
              subtitle="Validating data integrity"
              status={qcStatus}
              onRun={runQCOnly}
              runLabel="Run QC"
              disabled={!step3Enabled || qcStatus === "running"}
            />
            <PipelineCard
              title="Baseline Creation"
              subtitle="Calculating historical norms"
              status={baselineStatus}
              onRun={runBaselineOnly}
              runLabel="Run Baseline"
              disabled={!step3Enabled || baselineStatus === "running"}
            />
            <PipelineCard
              title="Inventory Model"
              subtitle="Running optimization algorithms"
              status={analysisStatus}
              onRun={runAnalysisOnly}
              runLabel="Run Analysis"
              disabled={!step3Enabled || analysisStatus === "running"}
            />
          </div>

          {/* <div className="pp-glass rounded-2xl border border-slate-800/70 p-4 mb-3 flex flex-col md:flex-row gap-4 md:items-end justify-between">
            <div className="min-w-0">
              <div className="font-semibold text-slate-100">QC Performance</div>
              <div className="text-slate-500 text-sm">
                Optional sampling to avoid timeouts. Leave blank to use the server default, or set to <span className="text-slate-300">0</span> for full QC.
              </div>
            </div>
            <label className="flex flex-col text-sm text-slate-300 w-full md:w-64">
              Sample rows
              <input
                type="number"
                min="0"
                step="1"
                className="pp-input mt-1"
                value={qcSampleRows}
                onChange={(e) => setQcSampleRows(e.target.value)}
                placeholder={storageStatus?.storage === "blob" ? "50000" : ""}
              />
            </label>
          </div> */}

          <div className="pp-glass rounded-2xl border border-slate-800/70 p-4 mb-3">
            <div className="font-semibold text-slate-100 mb-2">Segmentation / XYZ Thresholds</div>
            <div className="grid md:grid-cols-3 sm:grid-cols-2 grid-cols-1 gap-3">
              <label className="flex flex-col text-sm text-slate-300">
                Lookback months
                <input
                  type="number"
                  min="1"
                  max="36"
                  className="pp-input mt-1"
                  value={params.months}
                  onChange={(e) => setParams((p) => ({ ...p, months: Number(e.target.value) }))}
                />
              </label>
              <label className="flex flex-col text-sm text-slate-300">
                A threshold
                <input
                  type="number"
                  min="0.1"
                  max="0.99"
                  step="0.01"
                  className="pp-input mt-1"
                  value={params.a}
                  onChange={(e) => setParams((p) => ({ ...p, a: Number(e.target.value) }))}
                />
              </label>
              <label className="flex flex-col text-sm text-slate-300">
                B threshold
                <input
                  type="number"
                  min="0.1"
                  max="0.99"
                  step="0.01"
                  className="pp-input mt-1"
                  value={params.b}
                  onChange={(e) => setParams((p) => ({ ...p, b: Number(e.target.value) }))}
                />
              </label>
              <label className="flex flex-col text-sm text-slate-300">
                XYZ low percentile
                <input
                  type="number"
                  min="1"
                  max="49"
                  className="pp-input mt-1"
                  value={params.xyzLow}
                  onChange={(e) => setParams((p) => ({ ...p, xyzLow: Number(e.target.value) }))}
                />
              </label>
              <label className="flex flex-col text-sm text-slate-300">
                XYZ high percentile
                <input
                  type="number"
                  min="50"
                  max="99"
                  className="pp-input mt-1"
                  value={params.xyzHigh}
                  onChange={(e) => setParams((p) => ({ ...p, xyzHigh: Number(e.target.value) }))}
                />
              </label>
            </div>

            <div className="mt-4">
              <div className="font-semibold text-slate-100 mb-2">Safety Stock Caps (optional)</div>
              <div className="grid sm:grid-cols-2 grid-cols-1 gap-3">
                <label className="flex flex-col text-sm text-slate-300">
                  Min SS Days
                  <input
                    type="number"
                    min="0"
                    className="pp-input mt-1"
                    value={params.minSSDays}
                    onChange={(e) =>
                      setParams((p) => ({
                        ...p,
                        minSSDays: e.target.value === "" ? "" : Number(e.target.value),
                      }))
                    }
                  />
                </label>
                <label className="flex flex-col text-sm text-slate-300">
                  Max SS Days
                  <input
                    type="number"
                    min="0"
                    className="pp-input mt-1"
                    value={params.maxSSDays}
                    onChange={(e) =>
                      setParams((p) => ({
                        ...p,
                        maxSSDays: e.target.value === "" ? "" : Number(e.target.value),
                      }))
                    }
                  />
                </label>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-3 mb-3">
            <button
              type="button"
              onClick={runPipeline}
              disabled={
                !step3Enabled ||
                qcStatus === "running" ||
                baselineStatus === "running" ||
                analysisStatus === "running"
              }
              className="pp-btn disabled:opacity-50"
            >
              {pipelineRunning ? (
                <span className="inline-flex items-center gap-2">
                  <span className="pp-spinner" aria-hidden="true" /> Running pipeline
                </span>
              ) : (
                "Run Full Pipeline"
              )}
            </button>
          </div>

          <div className="mt-2 flex flex-wrap gap-3 justify-end items-center">
            {baselineLink ? (
              <a
                href={apiUrl(baselineLink)}
                className="pp-chip"
              >
                Download Baseline
              </a>
            ) : null}
            {analysisLink ? (
              <a
                href={apiUrl(analysisLink)}
                className="pp-chip"
              >
                Download Analysis
              </a>
            ) : null}
          </div>

          {!analysisLink ? (
            <div className="text-sm text-slate-400 text-right mt-1">
              Download link will appear here after a successful analysis run.
            </div>
          ) : null}
        </StepShell>

        {qcSummary.length ? (
          <div className="text-sm text-slate-300">
            <div className="font-semibold mb-1">QC Preview (first rows/errors)</div>
            <pre className="pp-glass border border-slate-800/70 rounded-2xl p-3 overflow-auto max-h-64 text-slate-200 text-xs">
              {JSON.stringify(qcSummary.slice(0, 10), null, 2)}
            </pre>
          </div>
        ) : null}

        {serviceLevels.length ? (
          <div className="pp-glass rounded-2xl border border-slate-800/70 p-4">
            <div className="font-semibold text-slate-100 mb-2">Service Levels</div>
            <div className="overflow-auto">
              <table className="min-w-full text-sm text-slate-100">
                <thead>
                  <tr className="text-left">
                    {Object.keys(serviceLevels[0]).map((col) => (
                      <th key={col} className="px-2 py-1 border-b border-slate-800">
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {serviceLevels.map((row, rIdx) => (
                    <tr key={rIdx} className="border-b border-slate-900">
                      {Object.entries(row).map(([col, val]) => (
                        <td key={col} className="px-2 py-1">
                          <input
                            className="pp-input w-full text-sm"
                            value={val}
                            onChange={(e) => {
                              const next = [...serviceLevels];
                              next[rIdx] = { ...next[rIdx], [col]: e.target.value };
                              setServiceLevels(next);
                            }}
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}
      </main>

      {toast ? <Toast msg={toast.msg} err={toast.err} /> : null}

      {pipelineRunning ? (
        <div className="fixed inset-0 z-40 bg-black/35 backdrop-blur-md flex items-end sm:items-center justify-center p-4">
          <div className="pp-glass border border-slate-800/70 rounded-3xl px-5 py-4 shadow-lg shadow-black/50 pp-animate-in w-[min(520px,calc(100vw-32px))]">
            <div className="flex items-start gap-3">
              <div className="pp-spinner mt-1" aria-hidden="true" />
              <div className="min-w-0">
                <div className="text-slate-100 font-semibold text-lg">
                  {pipelineKind === "qc"
                    ? "Quality check"
                    : pipelineKind === "baseline"
                    ? "Baseline"
                    : pipelineKind === "analysis"
                    ? "Analysis"
                    : "Pipeline"}
                </div>
                <div className="text-slate-400 text-sm">
                  {pipelineHint || "Working… this can take a few minutes on large datasets."}
                </div>
              </div>
            </div>
            <div className="pp-progress mt-4" aria-hidden="true" />
            <div className="mt-3 text-xs text-slate-500">
              Keep this tab open while the pipeline runs.
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
